import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Select, TextField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import { Alert, Card, CardBody, CardFooter, CardSection, FormActions } from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { isNotFound } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateFlagUserMutation,
  useGetFlagUserQuery,
  useUpdateFlagUserMutation,
} from '~/modules/query/featureFlags';

import { useReference } from '~/components/pages/feature-flags/useReference';
import type { KnownUser } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

interface Draft {
  product: string;
  environment: string;
  userId: string;
  customerId: string;
  name: string;
  email: string;
  phone: string;
}

const required = (message: string) => z.string().trim().min(1, message);

const schema = z.object({
  product: required('Choose a product.'),
  environment: required('Choose an environment.'),
  userId: required('Enter the user ID.'),
  customerId: required('Enter the customer ID.'),
  name: required('Enter the name.'),
  email: required('Enter the email.'),
  phone: required('Enter the phone number.'),
});

const FORM_ID = 'known-user-form';
const LIST = routes.featureFlagsSection('users');

/** Register (`/feature-flags/users/new`) and edit (`/feature-flags/users/:id`) one known user; `?product=` and `?environment=` pre-fill a new one (DESIGN.md §1.6). */
export default function KnownUserPage() {
  const navigate = useNavigate();
  const url = useUrlParams();
  const { id } = useParams<{ id: string }>();
  const rowId = id ?? '';
  const isNew = !id;
  const ref = useReference();
  const { environments, products } = ref.reference;

  // A new user starts in the list's product and environment (its defaults when the link names none).
  const defaults = {
    product: products.find((p) => p.key === url.get('product'))?.key ?? products[0]?.key ?? '',
    environment:
      environments.find((e) => e.key === url.get('environment'))?.key ??
      environments.find((e) => e.key === 'production')?.key ??
      environments[0]?.key ??
      '',
  };

  const query = useGetFlagUserQuery(rowId, { skip: isNew });
  const user = query.currentData;
  // The API exposes no version, so a record reseeds on id only; `markSaved` after a save.
  const { draft, patch, dirty, markSaved } = useRecordDraft<KnownUser | null, Draft>(
    ref.loaded ? (isNew ? null : user) : undefined,
    {
      id: id ?? 'new',
      toDraft: (u) => ({
        product: u?.product ?? defaults.product,
        environment: u?.environment ?? defaults.environment,
        userId: u?.userId ?? '',
        customerId: u?.customerId ?? '',
        name: u?.name ?? '',
        email: u?.email ?? '',
        phone: u?.phone ?? '',
      }),
    },
  );
  const guard = useUnsavedGuard(dirty);
  const check = useFormValidation(schema, draft, (field) => `user-${field}`);

  const [createUser] = useCreateFlagUserMutation();
  const [updateUser] = useUpdateFlagUserMutation();
  const save = useRecordSave<Draft>({
    save: async (d) => {
      const context = {
        ...user?.context,
        product: d.product,
        userId: d.userId.trim(),
        customerId: d.customerId.trim(),
        name: d.name.trim(),
        email: d.email.trim(),
        phone: d.phone.trim(),
      };
      if (user)
        await updateUser({
          id: user.id,
          data: { environment: user.environment, context },
        }).unwrap();
      else await createUser({ environment: d.environment, context }).unwrap();
    },
    success: isNew ? 'User created' : 'User saved',
    failure: 'The user could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(LIST, { replace: true });
  };

  const text = (key: keyof Draft) => ({
    value: draft?.[key] ?? '',
    onChange: (value: string) => patch({ [key]: value }),
    onBlur: () => check.touch(key),
    error: check.errors[key],
  });

  const title = isNew ? 'Create user' : user?.name || user?.userId || 'User';
  const notFound = !isNew && isNotFound(query.error);
  const loadError = ref.loaded ? null : ref.error;
  const failed = (query.isError && !notFound) || !!loadError;

  const fields = draft && (
    <CardSection
      title="Identity"
      description="Match these to the context your app sends. A later identity sync can update the contact details and customer account."
    >
      <div className="min-w-0">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Select
            id="user-product"
            label="Product"
            required
            value={draft.product}
            options={products.map((p) => ({ value: p.key, label: p.name }))}
            onChange={(value) => patch({ product: value })}
            disabled={!isNew}
            helperText={
              check.errors.product ??
              (isNew ? undefined : 'Product and environment are fixed after a user is registered.')
            }
          />
          <Select
            id="user-environment"
            label="Environment"
            required
            value={draft.environment}
            options={environments.map((e) => ({ value: e.key, label: e.name }))}
            onChange={(value) => patch({ environment: value })}
            disabled={!isNew}
            helperText={check.errors.environment}
          />
          <TextField
            id="user-userId"
            testId="user-user-id"
            label="User ID"
            required
            code
            readOnly={!isNew}
            {...text('userId')}
          />
          <TextField
            id="user-customerId"
            testId="user-customer-id"
            label="Customer ID"
            required
            code
            {...text('customerId')}
          />
          <div className="sm:col-span-2">
            <TextField id="user-name" testId="user-name" label="Name" required {...text('name')} />
          </div>
          <TextField
            id="user-email"
            testId="user-email"
            label="Email"
            type="email"
            required
            {...text('email')}
          />
          <TextField
            id="user-phone"
            testId="user-phone"
            label="Phone"
            required
            {...text('phone')}
          />
        </div>
      </div>
    </CardSection>
  );

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Known users', href: LIST },
        { label: title },
      ]}
      loading={!draft}
      notFound={notFound}
      notFoundTitle="User not found"
      backTo={LIST}
      backLabel="Back to known users"
      error={
        failed ? { what: 'the user', message: queryErrorMessage(query.error ?? loadError) } : null
      }
      onRetry={() => {
        if (query.isError) query.refetch();
        ref.refetch();
      }}
      guard={guard}
      testId="known-user"
    >
      {draft && (
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <Card>
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              {fields}
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={LIST}
                {...(isNew
                  ? { saveLabel: 'Create user', busyLabel: 'Creating…' }
                  : { disabled: !dirty, disabledReason: 'No changes to save' })}
                busy={save.busy}
                saveTestId="user-save"
              />
            </CardFooter>
          </Card>
        </form>
      )}
    </RecordPageShell>
  );
}
